import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { parseApiError } from '../../lib/apiErrors';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const token = searchParams.get('token');
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({ 
        title: 'Erreur', 
        description: 'Les mots de passe ne correspondent pas',
        variant: 'destructive' 
      });
      return;
    }

    if (password.length < 6) {
      toast({ 
        title: 'Erreur', 
        description: 'Le mot de passe doit contenir au moins 6 caractères',
        variant: 'destructive' 
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast({ 
        title: 'Succès', 
        description: 'Votre mot de passe a été modifié' 
      });
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: parseApiError(error),
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#285BBB] mx-auto mb-4" />
          <p className="text-[#6777A1]">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 text-center max-w-md"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3C65] mb-2">Lien invalide</h1>
          <p className="text-[#6777A1] mb-6">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <Link to="/forgot-password">
            <Button className="bg-[#285BBB] hover:bg-[#2C3C65] text-white">
              Demander un nouveau lien
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 text-center max-w-md"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3C65] mb-2">Mot de passe modifié !</h1>
          <p className="text-[#6777A1] mb-4">
            Vous allez être redirigé vers la page de connexion...
          </p>
          <Link to="/login">
            <Button className="bg-[#285BBB] hover:bg-[#2C3C65] text-white">
              Se connecter maintenant
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="The English Speaking Center" 
              className="h-16 mx-auto mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-2xl font-bold text-[#2C3C65]">Nouveau mot de passe</h1>
            <p className="text-[#6777A1] mt-2">
              Créez un nouveau mot de passe pour <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-10 pr-10"
                  data-testid="reset-password-new"
                />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-10"
                  data-testid="reset-password-confirm"
                />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-red-500 text-sm mt-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || password !== confirmPassword}
              className="w-full bg-[#285BBB] hover:bg-[#2C3C65] text-white py-3"
              data-testid="reset-password-submit"
            >
              {loading ? 'Modification...' : 'Modifier le mot de passe'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
